// ═══════════════════════════════════════════════════════════════════
// FieldPill.js — SHIP 9 · THE PILL ON THE SHATTERFIELD
// ═══════════════════════════════════════════════════════════════════
// caP'n, 2026-09-16: "we don't want the screen cluttered though, make
// it a pill you can expand."
//
// So: ONE small pill, top-centre, under nothing and over nothing —
// the top-left cluster is YOUR TURN / code, the top-right is T / timer
// / 3D, and the middle of that strip was empty. Collapsed it is one
// word wide. Pressed, it drops a scroll panel with two tabs:
//   · CODEX — what you are expected to memorise (fieldCodex.codexEntries)
//   · PLAYS — every effect you could press RIGHT NOW, and every one you
//     can't, with the gate's own reason (fieldCodex.legalPlaysFor).
// Escape or the pill closes it. Closed, the panel is UNMOUNTED — no
// invisible DOM over the 3D board eating pointer events, no per-frame
// legality math for a panel nobody is reading. The plays are computed
// only while the panel is open, on the render it is open in: the gate
// closures (`stampsFor`, `gateCtx`) are rebuilt by Shatterfield every
// render, so memoising on them would be a lie; the whole computation
// is a few dozen pure gate calls, cheaper than one card shadow.
//
// THE DOOR (second ask, same day): a legal row is a button. Pressing it
// hands the row's `play` descriptor to `onPlay`, which is Shatterfield's
// `handlePillPlay` — the same handlers the rift, the seal and the pit
// call. The pill closes first so the picker or radial the play opens
// lands on a clear board. Without `onPlay` (the review room) rows are
// read-only, exactly as before.
// ═══════════════════════════════════════════════════════════════════
import React, { useEffect, useMemo, useState } from "react";
import { codexEntries, legalPlaysFor } from "./fieldCodex";

// The manuscript chrome palette (Shatterfield.js `GM`, FieldChrome3D.js
// carries the same TODO to promote it to theme.js). Same hexes, by hand.
const P = {
  inkGold: "#c8963c", inkGoldBright: "#e8b84b", inkGoldDim: "#8a6520",
  vellum: "#e8d5a3", vellumDim: "#b89a5a", vellumFaint: "#7a6040",
  red: "#c0392b", green: "#6fbf73",
  font: "'Cinzel', 'Palatino Linotype', serif",
  body: "'Cormorant Garamond', 'Palatino Linotype', serif",
};

const PILL_Z = 40;   // above the floating chrome (20) and the corner clusters (30), below every modal (150+)

export default function FieldPill({
  me, opponent, turn = 0, seatCount = 2, myTurn = false,
  stampsFor = null, gateCtx = null, responseOpen = false, onPlay = null,
}) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("plays");
  const [filter, setFilter] = useState("");

  // Escape closes — and only while open, so the listener is not a
  // permanent tenant on window.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const codex = useMemo(() => codexEntries(seatCount), [seatCount]);

  // Computed only while open (see header). Guarded: the pill must never
  // be the thing that takes the Shatterfield down.
  let groups = [];
  let playsError = null;
  if (open && tab === "plays") {
    try {
      groups = legalPlaysFor({ me, opponent, turn, seatCount, myTurn, stampsFor, gateCtx, responseOpen });
    } catch (e) { playsError = String(e?.message || e); }
  }
  const q = filter.trim().toLowerCase();
  const hit = (s) => !q || String(s || "").toLowerCase().includes(q);
  const hitItem = (it) => hit(it.label) || hit(it.owner) || hit(it.text);
  const shown = groups.map((g) => ({ ...g, items: g.items.filter(hitItem) }));
  const readyCount = shown.reduce((n, g) => n + g.items.filter((it) => it.legal).length, 0);
  const anyShown = shown.some((g) => g.items.length || g.error);
  const canPlay = typeof onPlay === "function";
  const press = (it, e) => {
    if (!canPlay || !it.legal || !it.play) return;
    setOpen(false);
    onPlay(it.play, { clientX: e.clientX, clientY: e.clientY, label: it.label });
  };

  return (
    <div style={{ position: "absolute", top: 8, left: "50%", transform: "translateX(-50%)", zIndex: PILL_Z, display: "flex", flexDirection: "column", alignItems: "center", pointerEvents: "none" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={open ? "Close the Codex" : "Codex · every rule you are meant to know, and every play you can make right now"}
        aria-expanded={open}
        style={{
          pointerEvents: "auto", cursor: "pointer",
          padding: "5px 14px", borderRadius: 999,
          background: open ? `linear-gradient(135deg, ${P.inkGold}33, rgba(10,8,4,0.9))` : "linear-gradient(180deg, rgba(10,8,4,0.85), rgba(10,8,4,0.45))",
          border: `1px solid ${open ? P.inkGold : P.inkGold + "44"}`,
          boxShadow: `0 0 0 1px ${P.inkGold}0a, 0 2px 10px rgba(0,0,0,0.5)`,
          backdropFilter: "blur(10px)",
          color: open ? P.inkGoldBright : P.vellumDim,
          fontFamily: P.font, fontSize: 10, letterSpacing: 2, fontWeight: 700,
          transition: "all 0.2s",
        }}
      >
        {open ? "✕ CODEX" : "✦ CODEX · PLAYS"}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Field Codex"
          style={{
            pointerEvents: "auto", marginTop: 6, width: 440, maxWidth: "calc(100vw - 32px)",
            maxHeight: "72vh", display: "flex", flexDirection: "column",
            background: "linear-gradient(180deg, rgba(14,11,6,0.96), rgba(10,8,4,0.94))",
            border: `1px solid ${P.inkGold}55`, borderRadius: 8,
            boxShadow: `0 0 0 1px ${P.inkGold}12, 0 12px 40px rgba(0,0,0,0.7)`,
            backdropFilter: "blur(12px)", overflow: "hidden",
          }}
        >
          <style>{`.sf-pill-play:hover{background:rgba(200,150,60,0.10)} .sf-pill-play:focus{outline:1px solid #c8963c66}`}</style>
          {/* tabs */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 10px 6px", borderBottom: `1px solid ${P.inkGold}22` }}>
            {[["plays", `PLAYS${tab === "plays" && !playsError ? ` · ${readyCount} READY` : ""}`], ["codex", "CODEX"]].map(([id, label]) => (
              <button key={id} type="button" onClick={() => setTab(id)} style={{
                cursor: "pointer", padding: "4px 10px", borderRadius: 4,
                background: tab === id ? `${P.inkGold}22` : "transparent",
                border: `1px solid ${tab === id ? P.inkGold : P.inkGold + "33"}`,
                color: tab === id ? P.inkGoldBright : P.vellumDim,
                fontFamily: P.font, fontSize: 9.5, letterSpacing: 1.5, fontWeight: 700,
              }}>{label}</button>
            ))}
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="filter…"
              style={{
                marginLeft: "auto", width: 120, padding: "3px 8px", borderRadius: 4,
                background: "rgba(0,0,0,0.35)", border: `1px solid ${P.inkGold}33`,
                color: P.vellum, fontFamily: P.body, fontSize: 13, outline: "none",
              }}
            />
          </div>

          {/* body */}
          <div style={{ overflowY: "auto", padding: "6px 12px 12px", fontFamily: P.body, color: P.vellum }}>
            {tab === "plays" ? (
              <>
                <div style={{ fontSize: 12, color: P.vellumFaint, fontStyle: "italic", margin: "4px 0 8px" }}>
                  {myTurn ? "Your Turn. " : "Not your Turn — only what answers out of Turn is lit. "}
                  ● bright = the gate says yes right now · ○ dim = the gate's own reason · ◆ gold = a gift already in force, never pressed.
                  {canPlay ? " Press a bright row to play it — the same door as the rift, the seal or the pit." : " Press a play where it lives — the rift, the seal, the pit."}
                </div>
                {playsError && <div style={{ color: P.red, fontSize: 13 }}>The plays could not be read: {playsError}</div>}
                {!playsError && shown.map((g) => {
                  const items = g.items;
                  if (!items.length && !g.error) return null;
                  return (
                    <div key={g.title} style={{ marginBottom: 10 }}>
                      <div style={{ fontFamily: P.font, fontSize: 9.5, letterSpacing: 2, color: P.inkGold, fontWeight: 700, margin: "6px 0 4px", borderBottom: `1px solid ${P.inkGold}22`, paddingBottom: 3 }}>
                        {g.title.toUpperCase()} {items.some((it) => !it.passive) && <span style={{ color: P.vellumFaint, fontWeight: 400, letterSpacing: 1 }}>· {items.filter((it) => it.legal).length}/{items.filter((it) => !it.passive).length}</span>}
                      </div>
                      {g.error && <div style={{ color: P.red, fontSize: 12 }}>{g.error}</div>}
                      {items.map((it, k) => (
                        <div key={k} role={canPlay && it.legal && it.play ? "button" : undefined} tabIndex={canPlay && it.legal && it.play ? 0 : undefined}
                          onClick={(e) => press(it, e)}
                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); press(it, e); } }}
                          className={canPlay && it.legal && it.play ? "sf-pill-play" : undefined}
                          style={{ display: "grid", gridTemplateColumns: "1fr auto", columnGap: 8, padding: "4px 6px", margin: "0 -6px", borderRadius: 4, opacity: it.legal || it.passive ? 1 : 0.55, borderBottom: "1px solid rgba(255,255,255,0.03)", cursor: canPlay && it.legal && it.play ? "pointer" : "default" }}>
                          <div>
                            <span style={{ color: it.passive ? P.inkGold : it.legal ? P.green : P.vellumFaint, marginRight: 6 }}>{it.passive ? "◆" : it.legal ? "●" : "○"}</span>
                            <span style={{ fontWeight: 700, fontSize: 14, color: it.legal || it.passive ? P.vellum : P.vellumDim }}>{it.label}</span>
                            <div style={{ fontSize: 12, color: P.vellumDim, marginLeft: 16 }}>{it.owner}</div>
                            {it.text ? <div style={{ fontSize: 12.5, color: P.vellumDim, marginLeft: 16, fontStyle: "italic", opacity: 0.9 }}>{it.text}</div> : null}
                            {!it.legal && it.reason ? <div style={{ fontSize: 12, color: P.red, marginLeft: 16, opacity: 0.9 }}>{it.reason}</div> : null}
                          </div>
                          <div style={{ fontFamily: P.font, fontSize: 9.5, color: P.inkGoldBright, whiteSpace: "nowrap", alignSelf: "start", paddingTop: 3, textAlign: "right" }}>
                            {it.cost}
                            {canPlay && it.legal && it.play ? <div style={{ marginTop: 4, padding: "2px 8px", borderRadius: 3, border: `1px solid ${P.green}88`, color: P.green, fontSize: 9, letterSpacing: 1.5 }}>▶ PLAY</div> : null}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
                {!playsError && !anyShown && (
                  <div style={{ color: P.vellumFaint, fontStyle: "italic", fontSize: 13 }}>Nothing on your side of the field answers to that.</div>
                )}
              </>
            ) : (
              codex.map((sec) => {
                const entries = sec.entries.filter((e) => hit(e.title) || hit(e.text) || hit(sec.section));
                if (!entries.length) return null;
                return (
                  <div key={sec.section} style={{ marginBottom: 12 }}>
                    <div style={{ fontFamily: P.font, fontSize: 9.5, letterSpacing: 2, color: P.inkGold, fontWeight: 700, margin: "6px 0 3px", borderBottom: `1px solid ${P.inkGold}22`, paddingBottom: 3 }}>{sec.section.toUpperCase()}</div>
                    {sec.note ? <div style={{ fontSize: 12.5, color: P.vellumDim, fontStyle: "italic", margin: "2px 0 6px" }}>{sec.note}</div> : null}
                    {entries.map((e) => (
                      <div key={e.title} style={{ padding: "4px 0", borderBottom: "1px solid rgba(255,255,255,0.03)" }}>
                        <div style={{ fontWeight: 700, fontSize: 14 }}>{e.title}</div>
                        {e.text ? <div style={{ fontSize: 13, color: P.vellum, opacity: 0.9 }}>{e.text}</div> : null}
                        {e.meta ? <div style={{ fontFamily: P.font, fontSize: 9, letterSpacing: 1, color: P.inkGoldDim, marginTop: 2 }}>{e.meta}</div> : null}
                      </div>
                    ))}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
